import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AnimalSymbol from "@/components/AnimalSymbol";
import PedigreeTree from "@/components/PedigreeTree";
import Legend from "@/components/Legend";
import { BODY_PARTS, QUALITY_COLOR, emptyParts } from "@/lib/quality";
import { CONDITION_FIELDS } from "@/lib/conditions";
import { formatUS } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharePage() {
  const { token } = useParams();
  const [animal, setAnimal] = useState(null);
  const [pedigree, setPedigree] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await axios.get(`${API}/share/${token}`);
        if (cancelled) return;
        setAnimal(a.data);
        const p = await axios.get(`${API}/animals/${a.data.id}/pedigree?generations=4`);
        if (!cancelled) setPedigree(p.data);
      } catch {
        if (!cancelled) setError("This share link is invalid or has been rotated.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const ancestorsKeyed = useMemo(() => {
    if (!pedigree) return {};
    const out = {};
    Object.entries(pedigree.ancestors).forEach(([k, v]) => { out[parseInt(k, 10)] = v; });
    return out;
  }, [pedigree]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--app-text-soft)]">{error}</div>;
  if (!animal) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--app-bg)]">
      <header className="h-14 border-b border-[var(--app-border)] bg-white px-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-[var(--app-bg)] border border-[var(--app-border)] flex items-center justify-center">
          <AnimalSymbol parts={{ ears: "correct", head: "correct", neck: "correct", front: "correct", back: "correct", rear: "correct", tail: "correct" }} size={28} />
        </div>
        <div className="leading-tight">
          <div className="font-display font-extrabold text-[15px] tracking-tight">Vitality Pedigree</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--app-text-soft)]">Read-only share</div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-5" data-testid="share-page">
        <section className="bg-white border border-[var(--app-border)] rounded-md p-5 flex gap-5 items-center">
          <AnimalSymbol parts={animal.parts || emptyParts()} size={160} />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-3xl" data-testid="share-name">{animal.name}</div>
            <div className="text-sm text-[var(--app-text-soft)]">{animal.species_or_breed || "—"} · {animal.sex}{animal.dob ? ` · ${formatUS(animal.dob)}` : ""}</div>
            {animal.registration && <div className="font-mono-tech text-xs text-[var(--app-text-soft)] mt-1">{animal.registration}</div>}
            {animal.title && <div className="text-xs mt-1">{animal.title}</div>}
            {animal.notes && <p className="text-sm mt-2 whitespace-pre-line">{animal.notes}</p>}
          </div>
          <Legend />
        </section>

        <section className="bg-white border border-[var(--app-border)] rounded-md p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3">Structure</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {BODY_PARTS.map((bp) => {
              const q = (animal.parts || {})[bp.key] || "unknown";
              return (
                <div key={bp.key} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm border border-black/10" style={{ backgroundColor: QUALITY_COLOR[q] }} />
                  <span className="font-display font-semibold">{bp.label}:</span>
                  <span className="text-[var(--app-text-soft)] capitalize">{q.replace(/_/g, " ")}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-white border border-[var(--app-border)] rounded-md p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3">Health</div>
          {CONDITION_FIELDS.map((f) => (
            <div key={f.key} className="py-1 text-sm">
              <span className="inline-flex items-center gap-1.5 mr-2 align-middle">
                <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: f.color }} />
                <span className="font-display font-semibold">{f.label}:</span>
              </span>
              <span className="text-[var(--app-text-soft)]">{animal[f.key] || "—"}</span>
            </div>
          ))}
        </section>

        {pedigree && (
          <section className="bg-white border border-[var(--app-border)] rounded-md p-5 overflow-auto">
            <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3">Pedigree ({pedigree.generations} generations)</div>
            <PedigreeTree generations={pedigree.generations} ancestors={ancestorsKeyed} selectedIndex={null} onSelect={() => {}} />
          </section>
        )}
      </main>
    </div>
  );
}
